import { Injectable } from '@angular/core';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import { ConfigService } from './json-config.service';
import { LoggerService } from './logger/logger.service';
import { PostHog } from 'posthog-js'
import { interval, Subscription } from 'rxjs';

export interface PosthogUserInput {
  client_id: string,
  fullName: string,
  email?: string,
}

@Injectable({
  providedIn: 'root'
})
export class PosthogService {
  // Posthog can be throwing error to Sentry when user browser has installed AdBlock software
  // * ^ unwanted Sentry logging should be fixed with 1.0.18.18
  private posthog?: PostHog = (window as Window & { posthog?: PostHog }).posthog;
  private posthogInitSub?: Subscription;
  private posthogStartLogSub?: Subscription;

  // duplicit identify call prevention
  private alreadyLogging = false;

  public constructor(
    private configService: ConfigService,
    private loggerService: LoggerService,
  ) {}

  public init(): void {
    if(!this.posthogConfigExist()) {
      this.loggerService.logInfo('Posthog config missing, not initializing it.');
      return;
    }

    if(!this.posthog) {
      this.assignPosthogInstance();
    } else {
      try {
        this.posthog.init?.(
          this.configService.getConfig(EnvironmentKeys.posthogProjectApiKey),
          {
            api_host: this.configService.getConfig(EnvironmentKeys.posthogInstanceAddress),
            opt_in_site_apps: !!this.configService.getConfig(EnvironmentKeys.posthogOptInSiteApps),
          }
        );
      } catch (error) {
        this.loggerService.error('Failed to initialize Posthog analytic! ', error);
      }

      if(this.posthogInitSub) {
        this.posthogInitSub.unsubscribe();
      }
    }

  }

  private posthogConfigExist(): boolean {
    return !!this.configService.getConfig(EnvironmentKeys.posthogInstanceAddress) &&
    !!this.configService.getConfig(EnvironmentKeys.posthogOptInSiteApps)
  }

  public startLog(user: PosthogUserInput): void {
    if(this.posthog && this.posthogConfigExist() && !this.alreadyLogging && typeof this.posthog.identify === 'function') {
      try {
        this.posthog.identify?.(
          user.client_id,
          {
            name: user.fullName,
            email: user.email
          }
        );
        this.alreadyLogging = true;
      } catch (error) {
        this.loggerService.error('Failed to start Posthog logging! ', error);
        this.alreadyLogging = false;
      }

      if(this.posthogStartLogSub) {
        this.posthogStartLogSub.unsubscribe();
      }
    } else {
      if(this.posthogConfigExist() && !this.alreadyLogging) {
        this.assignPosthogInstance(user);
      }
    }
  }

  /**
   * Watch for window property assigned by 3rd side script for Posthog
   */
  private assignPosthogInstance(user?: PosthogUserInput): void {
    const sub = interval(1000)
      .subscribe(() => {
        const posthogInstance = (window as Window & { posthog?: PostHog }).posthog;
        if(posthogInstance) {
          this.posthog = posthogInstance;
        } else {
          this.loggerService.error('Failed to assign Posthog instance.')
        }
        if(user) {
          this.startLog(user);
        } else {
          this.init();
        }
      });

      if(user) {
        this.posthogStartLogSub = sub;
      } else {
        this.posthogInitSub = sub;
      }
  }

  public endLog(): void {
    if (this.posthog && this.posthogConfigExist()) {
      try {
        this.posthog.reset?.();
      } catch (error) {
        this.loggerService.error('Failed to close Posthog logging! ', error);
      }
    }
    this.alreadyLogging = false;
  }
}
